import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { useDispatch } from "react-redux";
import {
    Button,
    Checkbox,
    Dialog,
    DialogTitle,
    IconButton,
    Switch,
    Accordion,
    AccordionDetails,
    AccordionSummary
} from "@mui/material";
import { DoneRounded, CloseRounded, ExpandMore } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { connect } from "react-redux";
import { format } from "date-fns";

import { H2, H3, Loading } from "commons";
import { getFullClientName as getFullName } from "commons/src/helpers/FormattingHelpers";
import { isFailure, isSuccess } from "commons/src/helpers/RequestHelpers";
import { useComponentState } from "commons/src/helpers/CustomHooks";
import { isSmallScreen } from "commons/src/helpers/ContextHelpers";
import { nucleus } from "commons/src/NucleusTheme";
import { setUserPreferences } from "commons/src/reducers/clientPortalUserActions";

import {
    INFO as INFO_SEVERITY,
    WARNING as WARNING_SEVERITY,
    ERROR as ERROR_SEVERITY,
    getRefetchMessageAndAction,
    getFailedToFetchPreferencesMessage,
    getFailedToSetAdHocFeesMessage
} from "../../helpers/ErrorHelpers";
import { READONLY_MESSAGE } from "../../constants/messageText";
import { retryFetch, updateAdHocFeeAuth } from "../../components/DataOperations";
import { Message } from "../../components/Message";
import { requestObject } from "../../reducers/requestsActions";

function Modal({ children, isOpen, onClose }) {
    return (
        <Dialog open={isOpen} onClose={onClose}>
            <DialogTitle>
                <TitleH3 secondary>Ad-hoc fees ongoing authority</TitleH3>
                <StyledCloseButton onClick={onClose} aria-label="close modal">
                    <CloseRounded fontSize="large" color="secondary" />
                </StyledCloseButton>
            </DialogTitle>
            <DialogContentContainer data-testid="dialog-container">
                {children}
            </DialogContentContainer>
        </Dialog>
    );
}

Modal.propTypes = {
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.array]),
    isOpen: PropTypes.bool,
    onClose: PropTypes.func
};

function Controls({ isLoading, label, isAdviser }) {
    const [areTermsAgreed, setAreTermsAgreed] = useState(false);

    useEffect(() => {
        if (isLoading) {
            setAreTermsAgreed(false);
        }
    }, [isLoading]);

    return (
        <ControlsContainer isLoading={isLoading}>
            <Loading displayWhen={!isLoading} size={18}>
                <CheckboxContainer>
                    <Checkbox
                        size="small"
                        id="checkbox"
                        color="primary"
                        checked={areTermsAgreed}
                        onChange={() => setAreTermsAgreed(v => !v)}
                        disabled={isLoading}
                    />
                    <label htmlFor="checkbox">I agree</label>
                </CheckboxContainer>
                <ButtonContainer>
                    <StyledButton
                        type="submit"
                        disabled={!areTermsAgreed || isAdviser}
                        variant="contained"
                        color="primary"
                        disableElevation
                    >
                        {label}
                    </StyledButton>
                </ButtonContainer>
            </Loading>
        </ControlsContainer>
    );
}

function InfoSection({ displayJointAccountInfo }) {
    return (
        <Section>
            <P>
                Ad-hoc fees are one-off payments to your adviser. Before we allow these fees to be
                instructed by your adviser, we need your authorisation. You only need to give your
                ongoing authority once to enable this type of fee to be charged. Each time your
                adviser instructs an ad-hoc fee they must confirm with us that they have your
                approval to do so. You’ll also receive a confirmation letter every time these fees
                are charged and you can remove your authorisation at any time.
            </P>
            {displayJointAccountInfo ? (
                <>
                    <br />
                    <P>
                        Please note that because you hold a joint account with us, both parties will
                        have to provide authorisation separately before fees can be deducted from
                        your joint account.
                    </P>
                </>
            ) : null}
        </Section>
    );
}

InfoSection.propTypes = {
    displayJointAccountInfo: PropTypes.bool
};

function StatusSection({ isEnabled, adviserName, locationName, lastUpdated }) {
    return (
        <Section padded>
            <StatusHeader>
                {`We currently ${
                    isEnabled ? "" : "do not "
                }have authorisation to take ad-hoc fees from your account(s)`}
            </StatusHeader>
            {lastUpdated ? (
                <P>Last updated on: {format(new Date(lastUpdated), "dd/MM/yyyy")}</P>
            ) : null}
            <br />
            <P>Adviser name: {adviserName}</P>
            <P>Adviser firm: {locationName}</P>
        </Section>
    );
}

StatusSection.propTypes = {
    isEnabled: PropTypes.bool,
    adviserName: PropTypes.string,
    locationName: PropTypes.string,
    lastUpdated: PropTypes.string
};

function Form({ areAdHocFeesEnabled, isLoading, onSubmit, adviserName, locationName, isAdviser }) {
    const handleSubmit = (e, newState) => {
        e.preventDefault();
        if (!isAdviser) {
            onSubmit(newState);
        }
    };

    return (
        <>
            {!areAdHocFeesEnabled && (
                <form onSubmit={e => handleSubmit(e, true)}>
                    <P>By giving your authorisation, you agree to the following declaration:</P>
                    <List>
                        <ListItem>
                            {`I confirm that ${adviserName} is my duly authorised representative.`}
                        </ListItem>
                        <ListItem>
                            {`I authorise Nucleus to pay ${locationName} any ad-hoc fees as requested
                            by them via the Nucleus platform.`}
                        </ListItem>
                        <ListItem>
                            I understand that any ad-hoc fee paid will always be confirmed to me via
                            letter or email for validation purposes. You should contact Nucleus or
                            your adviser as soon as possible if any ad-hoc advice fees deducted from
                            your Nucleus wrap account are not what you have agreed with your
                            adviser.
                        </ListItem>
                        <ListItem>
                            I understand that I can rescind this ongoing authority at any time by
                            contacting Nucleus or logging into Nucleus Go and changing my
                            preference.
                        </ListItem>
                    </List>
                    <Controls isLoading={isLoading} label="Authorise" isAdviser={isAdviser} />
                </form>
            )}

            {areAdHocFeesEnabled && (
                <form onSubmit={e => handleSubmit(e, false)}>
                    <P>By removing your authorisation, you agree to the following declaration:</P>
                    <List>
                        <ListItem>
                            {`I confirm that ${adviserName} is my duly authorised representative.`}
                        </ListItem>
                        <ListItem>
                            {` I no longer authorise Nucleus to pay ${locationName} any ad-hoc fees as
                            requested by them via the Nucleus platform.`}
                        </ListItem>
                        <ListItem>
                            I understand that I can re-activate this authority at any time by
                            contacting Nucleus or logging into Nucleus Go and changing my
                            preference.
                        </ListItem>
                    </List>
                    <Controls
                        isLoading={isLoading}
                        label="Remove authorisation"
                        isAdviser={isAdviser}
                    />
                </form>
            )}
        </>
    );
}

Form.propTypes = {
    areAdHocFeesEnabled: PropTypes.bool,
    isLoading: PropTypes.bool,
    onSubmit: PropTypes.func,
    adviserName: PropTypes.string,
    locationName: PropTypes.string,
    isAdviser: PropTypes.bool
};

function AdHocFeesComponent({
    adviserName,
    areAdHocFeesEnabled,
    isAdviser,
    lastUpdated,
    locationName,
    getRequest,
    hasActiveJointAccounts
}) {
    const dispatch = useDispatch();
    const [errorMessage, setErrorMessage] = useState("");
    const [isExpanded, setIsExpanded] = useState(!isSmallScreen(nucleus.maxWidth));
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [componentState, changeStateTo, { DEFAULT, LOADING, ERROR, SUCCESS }] =
        useComponentState();

    const { palette = { status: {} } } = useTheme();
    const { status = {} } = palette;
    const { success, fail } = status;

    const successMessage = `You have now successfully updated your fee authorisation preferences. You can switch this ${
        areAdHocFeesEnabled ? "off" : "back on"
    } at any time by following the instructions below`;

    useEffect(() => {
        if (isFailure(getRequest.status)) {
            changeStateTo(ERROR);
        } else if (isSuccess(getRequest.status)) {
            changeStateTo(DEFAULT);
        }
    }, [getRequest.status, changeStateTo, ERROR, DEFAULT, LOADING]);

    const handleUpdatePermission = async enabled => {
        changeStateTo(LOADING);

        try {
            await dispatch(updateAdHocFeeAuth(enabled));

            changeStateTo(SUCCESS);
        } catch {
            changeStateTo(ERROR);
            setErrorMessage(getFailedToSetAdHocFeesMessage());
        }

        setIsFormOpen(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const { message: retryMessage, action } = getRefetchMessageAndAction(getRequest, () =>
        retryFetch(
            getRequest.href,
            setUserPreferences,
            "clientPreferences",
            getFailedToFetchPreferencesMessage()
        )
    );

    const handleSwitchClick = e => {
        e.stopPropagation();
        setIsFormOpen(true);
    };

    return (
        <Container>
            <MessageContainer>
                <Message
                    severity={INFO_SEVERITY}
                    text={successMessage}
                    shown={componentState === SUCCESS}
                />
            </MessageContainer>

            <MessageContainer>
                <Message
                    severity={ERROR_SEVERITY}
                    text={errorMessage || retryMessage}
                    action={action}
                    shown={componentState === ERROR}
                />
            </MessageContainer>

            <MessageContainer>
                <Message severity={WARNING_SEVERITY} text={READONLY_MESSAGE} shown={isAdviser} />
            </MessageContainer>

            <StyledH2 secondary textAlign="center">
                Authorisations
            </StyledH2>

            {isSuccess(getRequest.status) ? (
                <Accordion
                    square
                    elevation={0}
                    expanded={isExpanded}
                    onChange={(_, isOpen) => setIsExpanded(isOpen)}
                >
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <SummaryContainer>
                            <TitleContainer>
                                {areAdHocFeesEnabled ? (
                                    <DoneRoundedStyled htmlColor={success} />
                                ) : (
                                    <CloseRoundedStyled htmlColor={fail} />
                                )}
                                <TitleH3 secondary>Ad-hoc fees</TitleH3>
                            </TitleContainer>

                            <Switch
                                checked={areAdHocFeesEnabled}
                                color="primary"
                                onClick={handleSwitchClick}
                                aria-label="change ad hoc fees settings"
                            />
                        </SummaryContainer>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Content data-testid="ad-hoc-fees-page-content">
                            <InfoSection
                                displayJointAccountInfo={
                                    hasActiveJointAccounts && !areAdHocFeesEnabled
                                }
                            />
                            <StatusSection
                                isEnabled={areAdHocFeesEnabled}
                                lastUpdated={lastUpdated}
                                locationName={locationName}
                                adviserName={adviserName}
                            />
                            <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)}>
                                <Form
                                    areAdHocFeesEnabled={areAdHocFeesEnabled}
                                    isLoading={componentState === LOADING}
                                    onSubmit={handleUpdatePermission}
                                    locationName={locationName}
                                    adviserName={adviserName}
                                    isAdviser={isAdviser}
                                />
                            </Modal>
                        </Content>
                    </AccordionDetails>
                </Accordion>
            ) : null}
        </Container>
    );
}

const StatusHeader = styled(H3)`
    margin: 0;
`;

const StyledCloseButton = styled(IconButton)`
    && {
        position: absolute;
        top: 0;
        right: 0.5rem;

        @media (max-width: ${props => props.theme.maxWidth}) {
            right: 0;
        }
    }
`;

const DialogContentContainer = styled.div`
    padding: 0 1.5rem 1.5rem 1.5rem;
`;

const ControlsContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: ${props => (props.isLoading ? "flex-end" : "space-between")};
    align-items: center;
    margin-top: 1.5rem;

    @media (max-width: ${props => props.theme.maxWidth}) {
        flex-direction: column;
        justify-content: center;
        align-items: center;
    }
`;

const TitleH3 = styled(H3)`
    margin: 0;
`;

const TitleContainer = styled.div`
    display: flex;
    align-items: center;
`;

const SummaryContainer = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    justify-content: space-between;
`;

const DoneRoundedStyled = styled(DoneRounded)`
    && {
        font-size: 2rem;
        margin: -2px 0.5rem 0 0;
    }
`;

const CloseRoundedStyled = styled(CloseRounded)`
    && {
        font-size: 2rem;
        margin: -2px 0.5rem 0 0;
    }
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;

    && {
        .MuiPaper-root {
            margin-top: 0;
            width: 100%;
        }

        .MuiAccordion-root:before {
            background-color: transparent;
        }

        .MuiAccordionDetails-root {
            padding-top: 0;
        }
    }
`;

const MessageContainer = styled.section`
    width: 100%;
`;

const StyledButton = styled(Button)`
    && {
        @media (max-width: ${props => props.theme.maxWidth}) {
            width: 100%;
        }
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;

    @media (max-width: ${props => props.theme.maxWidth}) {
        flex: 1;
        justify-content: center;
        width: 100%;
    }
`;

const Section = styled.section`
    margin-bottom: 2rem;
    padding: ${props => (props.padded ? "1rem" : 0)};
    border: ${props => (props.padded ? `1px solid ${props.theme.primary}` : "none")};
`;

const StyledH2 = styled(H2)`
    margin: 1rem 0 1rem 0;
`;

const Content = styled.div`
    background: ${props => props.theme.tertiary};
    width: 100%;
    padding: 0 2.5rem;

    section:last-child {
        margin-bottom: 0;
    }

    @media (max-width: ${props => props.theme.maxWidth}) {
        margin-bottom: 1rem;
        align-self: flex-start;
        padding: 0;
    }
`;

const CheckboxContainer = styled.div`
    display: flex;
    align-items: center;

    @media (max-width: ${props => props.theme.maxWidth}) {
        margin-bottom: 1rem;
        align-self: flex-start;
    }
`;

const List = styled.ul`
    margin-top: 1rem;
    padding: 0 1rem;
`;

const ListItem = styled.li`
    font-size: 0.875rem;
`;

const P = styled.p`
    font-size: 0.875rem;
    margin: 0;
`;

Controls.propTypes = {
    areTermsAgreed: PropTypes.bool,
    isLoading: PropTypes.bool,
    onChange: PropTypes.func,
    label: PropTypes.string,
    isAdviser: PropTypes.bool
};

AdHocFeesComponent.propTypes = {
    adviserName: PropTypes.string,
    locationName: PropTypes.string,
    getRequest: requestObject,
    areAdHocFeesEnabled: PropTypes.bool,
    lastUpdated: PropTypes.string,
    updateAdHocFeeAuth: PropTypes.func,
    hasActiveJointAccounts: PropTypes.bool,
    isAdviser: PropTypes.bool
};

const mapStateToProps = state => ({
    adviserName: getFullName({
        forename: state.adviser.adviser.forename,
        surname: state.adviser.adviser.surname
    }),
    isAdviser: Boolean(state.user.adviserId),
    locationName: state.adviser.adviser.location.name,
    getRequest: state.requests.clientPreferences,
    areAdHocFeesEnabled: state.user.preferences.adHocFeeAuth.enabled,
    lastUpdated: state.user.preferences.adHocFeeAuth.lastUpdated,
    hasActiveJointAccounts:
        state?.accounts?.accounts &&
        Object.values(state.accounts.accounts).some(
            acc => Boolean(acc.jointClients) && acc.accountStatus !== "Closed"
        )
});

export const AdHocFees = connect(mapStateToProps)(AdHocFeesComponent);
